import { useEffect, useState } from 'react';
import type { ClockState, Side } from '@xiangqi/protocol';

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Remaining ms for `side` right now, interpolated from server data. */
export function remainingMs(
  clocks: ClockState,
  side: Side,
  serverOffset: number,
  running: boolean,
): number {
  const stored = side === 'red' ? clocks.red_ms : clocks.black_ms;
  if (!running || clocks.turn !== side || clocks.deadline_at === null) return stored;
  return Math.max(0, clocks.deadline_at - (Date.now() + serverOffset));
}

interface ClockProps {
  clocks: ClockState;
  side: Side;
  serverOffset: number;
  running: boolean;
}

export function Clock({ clocks, side, serverOffset, running }: ClockProps) {
  const [, tick] = useState(0);
  const active = running && clocks.turn === side && clocks.deadline_at !== null;

  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 200);
    return () => clearInterval(t);
  }, [active, clocks]);

  const ms = remainingMs(clocks, side, serverOffset, running);
  return (
    <span
      className={['clock', active ? 'active' : '', ms < 30_000 ? 'low' : ''].join(' ')}
      data-testid={`clock-${side}`}
    >
      {formatClock(ms)}
    </span>
  );
}
